import React, { useRef } from 'react'
import { motion, useInView, useScroll, useTransform } from 'framer-motion'
import ReliabilityEnterpriseSteps from '../molecules/EnterpriseComponents/ReliabilityEnterpriseSteps/ReliabilityEnterpriseSteps'
import EnterpriseIdeaProject from '../molecules/EnterpriseComponents/EnterpriseIdeaProject/EnterpriseIdeaProject'
import ChooseUs from '../molecules/ChooseUs/ChooseUs'
import AppsFaq from '../molecules/AppsFaq/AppsFaq'
import EnterpriseApplication from '../molecules/EnterpriseComponents/EnterpriseApplication/EnterpriseApplication'
import EnterpriseFeatures from '../molecules/EnterpriseComponents/EnterpriseFeatures/EnterpriseFeatures'
import images from '../../app/utlis/image'
import DefaultFooter from '../organisms/DefaultFooter/DefaultFooter'
import { getPageData, useData } from '../../app/helper/functions'

const EnterprisePage = () => {
    const { data } = useData();
    const enterpriseData = getPageData(data, 'enterprisePage');

    // Create refs for each section that will be animated
    const titleRef = useRef(null);
    const reliabilityRef = useRef(null);
    const ideaProjectRef = useRef(null);
    const featuresRef = useRef(null);
    const chooseUsRef = useRef(null);
    const faqRef = useRef(null);
    const applicationRef = useRef(null);
    
    // Check if sections are in view - set once to false to trigger animations every time element enters viewport
    const titleInView = useInView(titleRef, { once: false, amount: 0.3 });
    const reliabilityInView = useInView(reliabilityRef, { once: false, amount: 0.1 });
    const ideaProjectInView = useInView(ideaProjectRef, { once: false, amount: 0.1 });
    const featuresInView = useInView(featuresRef, { once: false, amount: 0.3 });
    const chooseUsInView = useInView(chooseUsRef, { once: false, amount: 0.3 });
    const faqInView = useInView(faqRef, { once: false, amount: 0.3 });
    const applicationInView = useInView(applicationRef, { once: false, amount: 0.3 });
    
    // Animation variants
    const fadeIn = {
        hidden: { opacity: 0 },
        visible: { opacity: 1, transition: { duration: 0.8 } }
    };
    
    const slideUp = {
        hidden: { y: 0, opacity: 0.6 },
        visible: { y: 10, opacity: 1, transition: { duration: 0.8, ease: "easeOut" } }
    };
    
    const slideIn = {
        hidden: { x: -70, opacity: 0 },
        visible: { x: 0, opacity: 1, transition: { duration: 0.7, ease: "easeOut" } }
    };
    
    const slideInRight = {
        hidden: { x: 70, opacity: 0 },
        visible: { x: 0, opacity: 1, transition: { duration: 0.7, ease: "easeOut" } }
    };

    return (
        <div className='overflow-x-hidden'>
            <div className='bg-enterprise-bg bg-center bg-cover bg-no-repeat'>
                <div className='bg-blackTransparent'>
                    <div className='flex flex-col md:flex-row pt-32 md:pt-52 pb-4'>
                        <motion.div 
                            ref={titleRef}
                            className='text-white flex flex-col gap-2 h-auto md:h-[700px] relative w-full md:w-[50%] px-4 md:pl-[9%]' 
                            initial="hidden"
                            animate={titleInView ? "visible" : "hidden"}
                            variants={slideIn}
                        >
                            <div className='capitalize font-bold text-4xl md:text-7xl leading-snug'>{enterpriseData.title}</div>
                            <div className='font-medium text-xl md:text-2xl'>{enterpriseData.description}</div>
                        </motion.div>
                        <motion.img
                            src={images.ENTERPRISEHOMEIMAGE}
                            className='w-full md:w-3/5 mt-8 md:mt-0'
                            initial="hidden"
                            animate={titleInView ? "visible" : "hidden"}
                            variants={slideInRight}
                        />
                    </div>
                </div>
            </div>
            <div className='bg-darkGreyPrimary'>
                <motion.div
                    ref={reliabilityRef}
                    initial="hidden"
                    animate={reliabilityInView ? "visible" : "hidden"}
                    variants={slideUp}
                    viewport={{ once: false, amount: 0.3 }}
                >
                    <ReliabilityEnterpriseSteps enterpriseData={enterpriseData} />
                </motion.div>
                <motion.div
                    ref={ideaProjectRef}
                    initial="hidden"
                    animate={ideaProjectInView ? "visible" : "hidden"}
                    variants={slideUp}
                    viewport={{ once: false, amount: 0.2 }}
                >
                    <EnterpriseIdeaProject enterpriseData={enterpriseData} />
                </motion.div>
                <motion.div
                    ref={featuresRef}
                    initial="hidden"
                    animate={featuresInView ? "visible" : "hidden"}
                    variants={slideUp}
                    viewport={{ once: false, amount: 0.3 }}
                >
                    <EnterpriseFeatures enterpriseData={enterpriseData} />
                </motion.div>
                <motion.div
                    ref={chooseUsRef}
                    initial="hidden"
                    animate={chooseUsInView ? "visible" : "hidden"}
                    variants={slideUp}
                    viewport={{ once: false, amount: 0.3 }}
                >
                    <ChooseUs />
                </motion.div>
                <motion.div
                    ref={faqRef}
                    initial="hidden"
                    animate={faqInView ? "visible" : "hidden"}
                    variants={slideUp}
                    viewport={{ once: false, amount: 0.3 }}
                >
                    <AppsFaq />
                </motion.div>
                <motion.div
                    ref={applicationRef}
                    initial="hidden"
                    animate={applicationInView ? "visible" : "hidden"}
                    variants={slideUp}
                    viewport={{ once: false, amount: 0.3 }}
                >
                    <EnterpriseApplication />
                </motion.div>
                <motion.div
                    initial="hidden"
                    whileInView="visible"
                    variants={fadeIn}
                    viewport={{ once: false, amount: 0.1 }}
                >
                    <DefaultFooter />
                </motion.div>
            </div>
        </div>
    )
}

export default EnterprisePage
